import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMilestoneQuestions, submitAnswers, IMilestoneDetail } from '@network/Questions';
import { getRaffle } from '@network/Raffle';
import { SCREEN } from '@type/index';
import { routes } from '@const/routes';
import { RaffleStatus } from '@type/Raffle';
import PopUp from '@components/PopUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { trackUserEvent, trackFunnel } from '@utils/analytics';
import useAnalytics from 'src/hooks/useAnalytics';

interface QuestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  milestoneId: number;
  milestoneTitle: string;
  onSuccess?: () => void;
}

const QuestionsModal: React.FC<QuestionsModalProps> = ({
  isOpen,
  onClose,
  milestoneId,
  milestoneTitle,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<IMilestoneDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const { trackButtonClick } = useAnalytics();
  const [error, setError] = useState<string | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: number]: number }>({});
  const [isCompleted, setIsCompleted] = useState(true);

  useEffect(() => {
    if (isOpen && milestoneId) {
      fetchQuestions();
      // Reset state when modal opens
      setCurrentQuestionIndex(0);
      setSelectedAnswers({});
      setIsCompleted(false);
    }
  }, [isOpen, milestoneId]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMilestoneQuestions(milestoneId);
      setQuestions(data);
      if (data?.questions?.length > 0) {
        trackFunnel.questionStart(milestoneId, 0, data.questions.length);
      }
    } catch (err: any) {
      console.error('Error fetching questions:', err);
      setError(err?.message || 'Failed to fetch questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId: number, answerId: number) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerId
    }));
  };

  const [questionsStartTime] = useState(Date.now());

  const handleNextQuestion = async () => {
    if (questions && currentQuestionIndex < questions.questions.length - 1) {
      const nextIdx = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIdx);
      trackFunnel.questionStart(milestoneId, nextIdx, questions.questions.length);
    } else {
      // Submit answers when completing the last question
      try {
        const answers = Object.entries(selectedAnswers).map(([questionId, answerId]) => ({
          questionId: parseInt(questionId),
          answerId: answerId,
        }));

        await submitAnswers(milestoneId, answers);
        setIsCompleted(true);

        // Track question completion with time spent
        const timeSpentSec = Math.round((Date.now() - questionsStartTime) / 1000);
        trackFunnel.questionComplete(milestoneId, answers.length, timeSpentSec);
        
        trackUserEvent.ticketEarned("questions", questions?.questions?.length || 0);
        trackButtonClick("complete_questions", {
          milestone_id: milestoneId,
          questions_answered: answers.length,
          time_spent_sec: timeSpentSec
        });

        // Refetch ticket counts after successful submission
        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error('Error submitting answers:', error);
        // Still show completion screen even if submission fails
        setIsCompleted(true);
      }
    }
  };


  const handleSuccessClick = async () => {
    try {
      setLoading(true);
      const data = {
        limit: 10,
        offset: 0,
        type: "ALL",
        screen: SCREEN.DASHBOARD,
      };
      const response = await getRaffle(data);
      const allRaffles = response.data?.raffles;

      const dailyRaffles = allRaffles?.daily?.raffles || [];
      const weeklyRaffles = allRaffles?.weekly?.raffles || [];
      const raffles = [...dailyRaffles, ...weeklyRaffles];

      const activeRaffle = raffles.find((r: any) =>
        (r.status === RaffleStatus.ELIGIBLE || r.status === RaffleStatus.HAPPENING || r.status === 'ACTIVE') && !r.isPlayed
      );

      if (activeRaffle) {
        navigate(`${routes.raffle}/${activeRaffle.id}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error("Error finding raffle:", error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const currentQuestion = questions?.questions?.[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === (questions?.questions?.length || 0) - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-3 sm:p-4">
      <div className={`
      ${isCompleted ? 'bg-transparent' : 'bg-white'} 
      rounded-xl sm:rounded-2xl 
      ${isCompleted ? 'p-0' : '!p-2 md:!p-4 '} 
      ${isCompleted ? 'max-w-[90%] sm:max-w-[600px] md:max-w-[750px]' : 'max-w-[95%] sm:max-w-[500px] md:max-w-[600px]'} 
      w-full
      ${isCompleted ? 'min-w-0 sm:min-w-[400px] md:min-w-[550px]' : 'min-w-0'}
      ${isCompleted ? 'max-h-[85vh]' : 'max-h-[90vh] sm:max-h-[85vh] md:max-h-[80vh]'} 
      ${isCompleted ? 'overflow-hidden' : 'overflow-y-auto'} 
      relative
    `}>
        {/* Header - only show when not completed */}
        {!isCompleted && (
          <div className="flex justify-between items-center mb-4 sm:mb-5 md:mb-6 border-b border-gray-200 pb-3 sm:pb-4">
            <h2 className="m-0 text-lg sm:text-xl md:text-2xl font-bold text-[#1f2c2b]">
              Answer Questions to earn free Tickets!
            </h2>
            <button
              onClick={onClose}
              className="bg-transparent border-none text-2xl sm:text-3xl cursor-pointer text-gray-600 hover:text-gray-800 p-1 sm:p-1.5 transition-colors active:scale-95"
            >
              ×
            </button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-center py-8 sm:py-10 md:py-12">
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto" />
            <p className="mt-4 sm:mt-5 text-sm sm:text-base text-gray-600">Loading questions...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8 sm:py-10 md:py-12 text-red-600">
            <p className="text-sm sm:text-base mb-3 sm:mb-4">Error: {error}</p>
            <button
              onClick={fetchQuestions}
              className="bg-blue-500 text-white border-none py-2 px-4 sm:py-2.5 sm:px-5 rounded-lg cursor-pointer hover:bg-blue-600 transition-colors active:scale-95 text-sm sm:text-base"
            >
              Retry
            </button>
          </div>
        ) : questions ? (
          <div>
            {isCompleted ? (
              <PopUp
                visible={isOpen}
                title=""
                onCancel={onClose}
                onOverlayClick={onClose}
              >
                <div className="relative w-full rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl bg-white">
                  {/* Header Section with Gradient Background */}
                  <div className="bg-gradient-to-r from-[#4B975B] to-[#ACC992] px-6 py-8 sm:py-10 md:py-12 text-center relative">
                    {/* Decorative circles */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                      <div className="absolute bottom-0 right-0 w-40 h-40 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
                    </div>

                    {/* Close button */}
                    <button
                      className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-[#4B975B] transition-colors shadow-lg"
                      onClick={onClose}
                    >
                      <FontAwesomeIcon icon={['fas', 'xmark']} className="text-gray-700" />
                    </button>

                    {/* Ticket Icon */}
                    <div className="w-20 h-20 mx-auto mb-4 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center relative z-10">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
                      </svg>
                    </div>

                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-2 relative z-10">
                      Congratulations!
                    </h2>
                    <p className="text-white text-base sm:text-lg md:text-xl font-bold relative z-10">
                      You have just won a free raffle ticket!
                    </p>
                  </div>

                  {/* Content Section */}
                  <div className="px-6 py-6 sm:py-8 md:py-10 text-center bg-white">
                    {/* WHAM graphic */}
                    {/* <div className="mb-6 sm:mb-8">
                      <svg className="w-48 h-24 sm:w-64 sm:h-32 md:w-80 md:h-40 mx-auto" viewBox="0 0 300 150" fill="none" xmlns="http://www.w3.org/2000/svg">
                        
                    <path d="M150 20 L160 50 L190 50 L165 70 L175 100 L150 80 L125 100 L135 70 L110 50 L140 50 Z" fill="#FFD700" />
                    <path d="M150 30 L155 55 L180 55 L160 70 L165 95 L150 80 L135 95 L140 70 L120 55 L145 55 Z" fill="#FFC700" />

                    <text x="150" y="95" fontFamily="Arial Black, sans-serif" fontSize="72" fontWeight="900" fill="#DC143C" textAnchor="middle" transform="rotate(-5 150 75)">
                      WHAM!
                    </text>
                    <text x="150" y="92" fontFamily="Arial Black, sans-serif" fontSize="72" fontWeight="900" fill="#FF1744" textAnchor="middle" transform="rotate(-5 150 75)">
                      WHAM!
                    </text>

                    <line x1="20" y1="60" x2="60" y2="50" stroke="#4B975B" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
                    <line x1="30" y1="80" x2="70" y2="75" stroke="#4B975B" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
                    <line x1="280" y1="60" x2="240" y2="50" stroke="#4B975B" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
                    <line x1="270" y1="80" x2="230" y2="75" stroke="#4B975B" strokeWidth="3" strokeLinecap="round" opacity="0.6" />

                    {Array.from({ length: 20 }).map((_, i) => (
                      <circle key={i} cx={70 + (i % 5) * 35} cy={110 + Math.floor(i / 5) * 8} r="2" fill="#4B975B" opacity="0.6" />
                    ))}
                  </svg>
                </div> */}

                    {/* CTA Button */}
                    {/* <div className="flex flex-col sm:flex-row gap-4 justify-center items-center"> */}
                    <button
                      onClick={handleSuccessClick}
                      className="flex-1 bg-white text-[#4B975B] border-2 border-[#4B975B] font-black text-sm sm:text-base py-3 sm:py-4 px-6 sm:px-8 rounded-xl shadow-lg hover:bg-gray-50 transition-all duration-200 active:scale-95 uppercase tracking-wide w-full"
                    >
                      PLAY RAFFLE NOW!
                    </button>
                    {/* </div> */}

                    {/* Footer text */}
                    <p className="text-gray-500 text-[10px] sm:text-xs mt-4 sm:mt-6">
                      Prize cannot be redeemed for cash. *Terms and Conditions Apply.
                    </p>
                  </div>
                </div>
              </PopUp>
            ) : questions.questions && questions.questions.length > 0 ? (
              <div>
                {/* Progress indicator */}

                {/* Current question */}
                {currentQuestion && (
                  <div className="border border-gray-200 rounded-lg sm:rounded-xl !p-2 md:!p-4 bg-gray-50">
                    <h3 className="m-0 mb-3 sm:mb-4 text-base sm:text-lg md:text-xl font-semibold text-[#1f2c2b]">
                      {currentQuestion.title}
                    </h3>

                    <div>
                      <p className="m-0 mb-2 sm:mb-3 text-xs sm:text-sm font-semibold text-gray-700">
                        Select an answer:
                      </p>
                      {currentQuestion.answers && currentQuestion.answers.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                          {currentQuestion.answers.map((answer, answerIndex) => (
                            <label
                              key={answer.id}
                              className={`flex items-center !p-3 md:!p-4 ${selectedAnswers[currentQuestion.id] === answer.id ? 'bg-blue-50 border-2 border-blue-500' : 'bg-white border border-gray-300'} rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md active:scale-[0.98] min-h-[56px] sm:min-h-[60px]`}
                            >
                              <input
                                type="radio"
                                name={`question_${currentQuestion.id}`}
                                value={answer.id}
                                checked={selectedAnswers[currentQuestion.id] === answer.id}
                                onChange={() => handleAnswerSelect(currentQuestion.id, answer.id)}
                                className="mr-2 sm:mr-2.5 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
                              />
                              {/* <span className="font-bold mr-2 sm:mr-2.5 text-blue-500 min-w-[20px] sm:min-w-[24px] text-sm sm:text-base">
                                {String.fromCharCode(65 + answerIndex)}.
                              </span> */}
                              <span className="flex-1 text-xs sm:text-sm md:text-base text-gray-800">
                                {answer.content}
                              </span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-400 italic text-sm sm:text-base">
                          No options available
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Navigation buttons */}
                <div className="flex justify-between items-center gap-3 sm:gap-4">
                  <button
                    onClick={handlePreviousQuestion}
                    disabled={isFirstQuestion}
                    className={`
                    ${isFirstQuestion ? 'bg-gray-100 text-gray-400' : 'bg-gray-600 text-white hover:bg-gray-700'} 
                    border-none py-2 px-4 sm:py-2.5 sm:px-5 md:py-3 md:px-6
                    rounded-full text-xs sm:text-sm md:text-base font-semibold
                    ${isFirstQuestion ? 'cursor-not-allowed opacity-60' : 'cursor-pointer active:scale-95'} 
                    transition-all duration-200
                  `}
                  >
                    Previous
                  </button>

                  <button
                    onClick={handleNextQuestion}
                    disabled={!selectedAnswers[currentQuestion?.id || 0]}
                    className={`
                    ${selectedAnswers[currentQuestion?.id || 0]
                        ? 'bg-gradient-to-r from-[#28A949] to-[#1FC693] text-white hover:shadow-lg'
                        : 'bg-gray-100 text-gray-400'
                      } 
                    border-none py-2 px-4 sm:py-2.5 sm:px-5 md:py-3 md:px-6
                    rounded-full text-xs sm:text-sm md:text-base font-semibold
                    ${selectedAnswers[currentQuestion?.id || 0] ? 'cursor-pointer active:scale-95' : 'cursor-not-allowed opacity-60'} 
                    transition-all duration-200
                  `}
                  >
                    {isLastQuestion ? 'Finish' : 'Next'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 sm:py-10 md:py-12 text-gray-600">
                <p className="text-sm sm:text-base">No questions available for this milestone.</p>
              </div>
            )}
          </div>
        ) : null
        }

      </div >

      <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
    </div >
  );
};

export default QuestionsModal;
